/**
 * ATTEST-UI-1 — the letter facts, the required inputs, and the statements.
 *
 * The assertion picker is limited to the selected type's allow list. That is a
 * convenience, not a control: the server re-checks every statement against the
 * policy, and a statement whose *prose* strays onto the deny list is refused
 * even though its assertion id was picked from this dropdown. That is the
 * intended behaviour — the id says what you meant, the scan reads what you
 * wrote — and it is why the refusal panel exists downstream of this screen.
 *
 * The letter's own facts and the type's `required_inputs` live in
 * `LetterFactsSection`; the §7216 consent block lives in `LetterConsentSection`.
 * Both were split out under `docs/FILE_SIZE_POLICY.md` — what stays here is the
 * statements and the evidence hanging off them.
 *
 * ATTEST-EVIDENCE-REQUIRED-1 adds the document checklist above the statements
 * and, per statement, the row it draws on. The checklist is derived, never
 * stored: `buildChecklist` reads the form and the policy, and nothing on this
 * screen decides whether the letter may issue.
 *
 * Item 3 threads `verification` through — the engine's answer for the last
 * build, or `null` if there has not been one. It is a prop rather than
 * something computed here because the answer belongs to the response, not to
 * the form, and this component holds no responses.
 */
import type { AttestPolicy, LetterTypeSummary, ParsedFigure } from '@/components/attest/api'
import {
  attestationOfferedFor, buildChecklist, rowsForStatement,
} from '@/components/attest/documentChecklist'
import { EvidenceBlock, formatFigure } from '@/components/attest/EvidenceBlock'
import type {
  EvidenceDraft, LetterFormPatch, LetterFormState, StatementDraft,
} from '@/components/attest/formState'
import { emptyStatement, EMPTY_EVIDENCE } from '@/components/attest/formState'
import { LetterConsentSection } from '@/components/attest/LetterConsentSection'
import { LetterFactsSection } from '@/components/attest/LetterFactsSection'
import { RequiredDocumentsChecklist } from '@/components/attest/RequiredDocumentsChecklist'
import { RequiredFieldsNote, RequiredMark } from '@/components/attest/requiredFields'
import { StatementEvidenceNote } from '@/components/attest/StatementEvidenceNote'
import type { VerificationMap } from '@/components/attest/verificationResult'
import { WIZARD_FIELD_HELP } from '@/components/attest/wizardHelp'
import { HelpDot } from '@/components/ui/help-dot'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

export interface LetterDetailsStepProps {
  letterType: LetterTypeSummary
  policy: AttestPolicy | null
  form: LetterFormState
  onChange: (patch: LetterFormPatch) => void
  /**
   * The engine's per-statement verdict from the last build, or `null` when it
   * has not judged this form. `null` is what keeps the checklist saying
   * "attached" rather than claiming a check nobody ran.
   */
  verification?: VerificationMap | null
}

export function LetterDetailsStep({
  letterType, policy, form, onChange, verification = null,
}: LetterDetailsStepProps) {
  const allowedLabels = new Map(
    (policy?.allowed_assertions ?? []).map(a => [a.id, a.label]),
  )

  // Derived on every render, deliberately: it is a fold over state that is
  // already in memory, and a memo keyed on `form.statements` would have to be
  // keyed on the evidence inside them too. A stale checklist is a checklist
  // that says "attached" about a document that was just removed.
  const checklist = buildChecklist(letterType, form.statements, policy, verification)

  /*
   * Every helper below patches a *nested* container, so each one derives that
   * container from `prev` rather than from the `form` prop, which is only a
   * render snapshot. Two patches sharing a snapshot lose the earlier write.
   *
   * This is a hardening, not a reproduced fix: the reported "step 3 loses
   * values on scroll" has no mechanism in this tree — nothing subscribes to
   * scroll, the page is a static import that cannot remount on navigation, the
   * form state lives in `LetterGenerator` and is never reset, and every input
   * here is controlled. Every current write also arrives on a discrete DOM
   * event, which React flushes synchronously, so the snapshot is fresh. If
   * values are genuinely being lost, the cause is still unfound and a repro is
   * needed. Read-only uses of `form` (rendering current values) are correct as
   * they are.
   */
  const setStatement = (index: number, patch: Partial<StatementDraft>) => {
    onChange(prev => ({
      statements: prev.statements.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }))
  }

  const setEvidence = (sIdx: number, eIdx: number, patch: Partial<EvidenceDraft>) => {
    onChange(prev => ({
      statements: prev.statements.map((s, i) => (
        i === sIdx
          ? { ...s, evidence: s.evidence.map((e, j) => (j === eIdx ? { ...e, ...patch } : e)) }
          : s
      )),
    }))
  }

  const addEvidence = (sIdx: number) => {
    onChange(prev => ({
      statements: prev.statements.map((s, i) => (
        i === sIdx ? { ...s, evidence: [...s.evidence, { ...EMPTY_EVIDENCE }] } : s
      )),
    }))
  }

  const removeEvidence = (sIdx: number, eIdx: number) => {
    onChange(prev => ({
      statements: prev.statements.map((s, i) => (
        i === sIdx ? { ...s, evidence: s.evidence.filter((_, j) => j !== eIdx) } : s
      )),
    }))
  }

  /*
   * A figure the parser read is appended to the sentence, never substituted into
   * it: the CPA is signing the sentence, so they get the number put where they
   * can see it and then write around it. Appending also makes a mis-click
   * trivially undoable — it's the tail of a textarea they already control.
   *
   * The click is recorded as well as rendered (ATTEST-EVIDENCE-REQUIRED-1 item
   * 3b). Which figure came out of which document is a fact the sentence itself
   * cannot carry: "$1,284,300" in the prose says a number was claimed, not that
   * *this* source reported it. Keeping the key lets the server re-read that one
   * document at build and confirm it still says so — the check for a file
   * swapped between the click and the signature. Last click on a row wins,
   * which matches what the CPA sees: one source, one figure attributed to it.
   */
  const insertFigure = (sIdx: number, eIdx: number, figure: ParsedFigure) => {
    onChange(prev => ({
      statements: prev.statements.map((s, i) => {
        if (i !== sIdx) return s
        const amount = formatFigure(figure.value)
        const text = s.text.trim()
        return {
          ...s,
          text: text ? `${text} ${amount}` : amount,
          evidence: s.evidence.map((e, j) => (
            j === eIdx
              ? { ...e, figure_key: figure.key, figure_value: figure.value }
              : e
          )),
        }
      }),
    }))
  }

  return (
    <div className="space-y-6">
      <RequiredFieldsNote />

      <LetterFactsSection letterType={letterType} form={form} onChange={onChange} />

      <RequiredDocumentsChecklist summary={checklist} />

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Statements</h3>
          <Button
            variant="outline"
            size="sm"
            data-testid="add-statement"
            onClick={() => onChange(prev => ({
              statements: [...prev.statements, emptyStatement()],
            }))}
          >
            Add statement
          </Button>
        </div>

        <div className="mt-3 space-y-4">
          {form.statements.map((statement, sIdx) => (
            <div key={sIdx} className="rounded-lg border border-border p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-1">
                  {/* One (i) for the list, on the first row. The help is the
                      same sentence for every statement, and a `data-testid`
                      repeated down the page is not a hook, it's a collision. */}
                  <div className="flex items-center gap-1">
                    <Label className="block" htmlFor={`assertion-${sIdx}`}>
                      Assertion
                      <RequiredMark field={`assertion-${sIdx}`} />
                    </Label>
                    {sIdx === 0 && <HelpDot {...WIZARD_FIELD_HELP.statementAssertion} testId="info-statementAssertion" />}
                  </div>
                  <select
                    id={`assertion-${sIdx}`}
                    data-testid={`assertion-${sIdx}`}
                    className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={statement.assertion_id}
                    onChange={e => setStatement(sIdx, { assertion_id: e.target.value })}
                  >
                    <option value="">Select an assertion…</option>
                    {letterType.allowed_assertions.map(id => (
                      <option key={id} value={id}>{allowedLabels.get(id) ?? id}</option>
                    ))}
                  </select>
                </div>
                {form.statements.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`remove-statement-${sIdx}`}
                    onClick={() => onChange(prev => ({
                      statements: prev.statements.filter((_, i) => i !== sIdx),
                    }))}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <div className="mt-3">
                <Label className="block" htmlFor={`statement-text-${sIdx}`}>
                  Sentence as it will appear
                  <RequiredMark field={`statement-text-${sIdx}`} />
                </Label>
                <Textarea
                  id={`statement-text-${sIdx}`}
                  data-testid={`statement-text-${sIdx}`}
                  rows={2}
                  className="mt-1"
                  value={statement.text}
                  onChange={e => setStatement(sIdx, { text: e.target.value })}
                />
              </div>

              <StatementEvidenceNote
                rows={rowsForStatement(checklist, sIdx)}
                attestationOffered={attestationOfferedFor(checklist, sIdx)}
                statementIndex={sIdx}
                checked={statement.supportedByRecordsOnFile}
                onChange={checked => setStatement(sIdx, {
                  supportedByRecordsOnFile: checked,
                })}
              />

              <EvidenceBlock
                statementIndex={sIdx}
                evidence={statement.evidence}
                onChange={(eIdx, patch) => setEvidence(sIdx, eIdx, patch)}
                onAdd={() => addEvidence(sIdx)}
                onRemove={eIdx => removeEvidence(sIdx, eIdx)}
                onInsertFigure={(eIdx, figure) => insertFigure(sIdx, eIdx, figure)}
              />
            </div>
          ))}
        </div>
      </section>

      <LetterConsentSection form={form} onChange={onChange} />
    </div>
  )
}
